import React, { useState } from "react";
import {
  Box,
  Button,
  Typography,
  Paper,
  LinearProgress,
  IconButton,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
} from "@mui/material";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";
import { useBulkImportFaqsMutation } from "../../../Services/faqs/FAQs";

interface BulkUploadProps {
  faqs: any[];
  refetchFaqs: () => void;
}

const BulkUpload: React.FC<BulkUploadProps> = ({ faqs, refetchFaqs }) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [showingLoader, setShowingLoader] = useState(false);
  const [previewData, setPreviewData] = useState<any[]>([]);
  const [fileName, setFileName] = useState("");
  const [failedItems, setFailedItems] = useState<any[]>([]);

  const [triggerBulkImportFaqs] = useBulkImportFaqsMutation({});

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];

      console.log("=== FILE UPLOAD INITIATED ===");
      console.log("File name:", file.name);
      console.log("File type:", file.type);
      console.log("File size:", file.size, "bytes");

      // Validate file extensions
      const hasValidExtension =
        file.name.endsWith(".xlsx") ||
        file.name.endsWith(".xls") ||
        file.name.endsWith(".csv");

      if (!hasValidExtension) {
        toast.error("Please upload a valid file format: .xlsx, .xls, or .csv");
        return;
      }

      setSelectedFile(file);
      setFileName(file.name);
      setFailedItems([]); // Clear any previous failed items

      // Read and preview the file
      const reader = new FileReader();

      reader.onload = (e: ProgressEvent<FileReader>) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: "array" });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData: any[][] = XLSX.utils.sheet_to_json(worksheet, {
            header: 1,
          });

          console.log("Excel data parsed:", jsonData);
          console.log("Total rows:", jsonData.length);

          // Validate headers
          const headers = jsonData[0];
          if (!headers || headers.length < 2) {
            toast.error(
              "Invalid file format. File must have at least 2 columns: question and answer"
            );
            setPreviewData([]);
            setSelectedFile(null);
            setFileName("");
            return;
          }

          // Process data rows - include rows even if question or answer is missing
          const processedData: any[] = [];
          for (let i = 1; i < jsonData.length; i++) {
            const row = jsonData[i];
            // Include row if it has at least some data
            if (row[0] || row[1]) {
              processedData.push({
                question: row[0] ? String(row[0]).trim() : "",
                answer: row[1] ? String(row[1]).trim() : "",
                hasError: !row[0] || !row[1], // Flag rows with missing data
                rowNumber: i + 1,
              });
            }
          }

          console.log("Processed FAQs:", processedData);
          console.log("Valid FAQs found:", processedData.length);
          console.log(
            "FAQs with errors:",
            processedData.filter((item) => item.hasError).length
          );

          if (processedData.length === 0) {
            toast.error(
              "No valid FAQ data found. Please ensure your file has question and answer columns with data."
            );
            setPreviewData([]);
            setSelectedFile(null);
            setFileName("");
            return;
          }

          const errorCount = processedData.filter(
            (item) => item.hasError
          ).length;
          setPreviewData(processedData);

          if (errorCount > 0) {
            toast.warning(
              `Loaded ${processedData.length} FAQ(s) for preview. ${errorCount} row(s) have missing question or answer.`,
              { autoClose: 5000 }
            );
          } else {
            toast.success(
              `Successfully loaded ${processedData.length} FAQ(s) for preview`
            );
          }
        } catch (error) {
          console.error("File reading error:", error);
          toast.error(
            "Error reading file. Please make sure it's a valid Excel or CSV file."
          );
          setPreviewData([]);
          setSelectedFile(null);
          setFileName("");
        }
      };

      reader.onerror = () => {
        toast.error("Failed to read the file. Please try again.");
        console.error("FileReader error");
      };

      reader.readAsArrayBuffer(file);
    }
    event.target.value = "";
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setPreviewData([]);
    setFileName("");
    setFailedItems([]);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    console.log("=== BULK UPLOAD SUBMIT ===");

    if (!selectedFile) {
      toast.error("Please select a file to upload");
      return;
    }

    if (previewData.length === 0) {
      toast.error("No valid data to upload");
      return;
    }

    setShowingLoader(true);

    try {
      // Create FormData with file - payload key is "file"
      const formData = new FormData();
      formData.append("file", selectedFile);

      console.log("Sending bulk upload request...");
      console.log("FormData payload:", {
        file: selectedFile.name,
        type: selectedFile.type,
        size: selectedFile.size,
        faqCount: previewData.length,
      });

      // Pass formData as body to match API structure
      const response: any = await triggerBulkImportFaqs({ body: formData });

      console.log("Full API Response:", response);

      if (response?.data) {
        console.log("Bulk upload complete!", response.data);

        const summary = response.data.summary;
        const failedItemsData = response.data.failed_items || [];

        if (summary) {
          // Show success message with details
          if (summary.fail_count > 0) {
            toast.warning(
              `Upload completed: ${summary.success_count} succeeded, ${summary.fail_count} failed out of ${summary.total_rows} total rows. Check below for failed items.`,
              { autoClose: 5000 }
            );

            // Store failed items to display
            setFailedItems(failedItemsData);
            console.error("Failed items:", failedItemsData);

            // Update preview to show only failed items
            const failedPreview = failedItemsData.map((item: any) => ({
              question: item.data?.question || item.question || "",
              answer: item.data?.answer || item.answer || "",
              hasError: true,
              rowNumber: item.row,
              errorMessage: item.error || item.reason || "Unknown error",
            }));

            setPreviewData(failedPreview);
          } else {
            toast.success(
              `Successfully uploaded ${summary.success_count} FAQ(s) out of ${summary.total_rows} total rows!`
            );
            setFailedItems([]);
            // Clear everything on complete success
            setSelectedFile(null);
            setPreviewData([]);
            setFileName("");
          }
        } else {
          toast.success(`Successfully uploaded FAQs!`);
          setFailedItems([]);
          // Clear everything on success
          setSelectedFile(null);
          setPreviewData([]);
          setFileName("");
        }

        // Refetch FAQs to show updated list if any succeeded
        if (!summary || summary.success_count > 0) {
          await refetchFaqs();
        }
      } else if (response?.error) {
        console.error("API Error Response:", response.error);

        const errorData = response?.error?.data;

        // Handle various error response structures
        let errorMessage = "Upload failed. Please try again.";
        let failedItemsFromError = [];

        if (errorData) {
          // Check for summary and failed_items in error response
          if (errorData.summary) {
            const summary = errorData.summary;
            if (summary.fail_count > 0) {
              toast.warning(
                `Upload completed: ${summary.success_count} succeeded, ${summary.fail_count} failed out of ${summary.total_rows} total rows. Check below for failed items.`,
                { autoClose: 5000 }
              );
              failedItemsFromError = errorData.failed_items || [];
              setFailedItems(failedItemsFromError);

              // Update preview to show only failed items
              const failedPreview = failedItemsFromError.map((item: any) => ({
                question: item.data?.question || item.question || "",
                answer: item.data?.answer || item.answer || "",
                hasError: true,
                rowNumber: item.row,
                errorMessage: item.error || item.reason || "Unknown error",
              }));

              setPreviewData(failedPreview);

              // Refetch FAQs if some succeeded
              if (summary.success_count > 0) {
                await refetchFaqs();
              }

              setShowingLoader(false);
              return;
            }
          }

          // Check for standard error messages
          const apiError =
            errorData.non_field_errors ||
            errorData.detail ||
            errorData.message ||
            errorData.error;

          if (Array.isArray(apiError) && apiError.length > 0) {
            errorMessage = apiError[0];
          } else if (typeof apiError === "string") {
            errorMessage = apiError;
          }
        } else if (response?.error?.message) {
          errorMessage = response.error.message;
        }

        toast.error(errorMessage);
      } else {
        toast.error("Upload failed. Please try again.");
      }
    } catch (error: any) {
      console.error("Bulk upload failed:", error);
      toast.error("An error occurred during upload");
    } finally {
      setShowingLoader(false);
    }
  };

  const handleDownloadTemplate = () => {
    console.log("=== DOWNLOADING TEMPLATE ===");

    // Create template with only headers
    const template = [["question", "answer"]];

    const ws = XLSX.utils.aoa_to_sheet(template);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "FAQ Template");
    XLSX.writeFile(wb, "faq_template.xlsx");
    console.log("Template downloaded: faq_template.xlsx");
  };

  const handleDownloadFailedItems = () => {
    console.log("=== DOWNLOADING FAILED ITEMS ===");

    if (failedItems.length === 0) {
      toast.info("No failed items to download");
      return;
    }

    // Create Excel data with headers and failed items
    const excelData = [
      ["question", "answer", "error_message"],
      ...failedItems.map((item: any) => [
        item.data?.question || item.question || "",
        item.data?.answer || item.answer || "",
        item.error || item.reason || "Unknown error",
      ]),
    ];

    const ws = XLSX.utils.aoa_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Failed FAQs");

    const timestamp = new Date()
      .toISOString()
      .replace(/[:.]/g, "-")
      .slice(0, -5);
    const filename = `failed_faqs_${timestamp}.xlsx`;

    XLSX.writeFile(wb, filename);
    console.log("Failed items downloaded:", filename);
    toast.success(`Downloaded ${failedItems.length} failed FAQ(s)`);
  };

  return (
    <form onSubmit={handleSubmit}>
      <Box
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          padding: 4,
          bgcolor: "#fef5f1",
        }}
      >
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h5"
            gutterBottom
            color="#666666"
            fontFamily="Karbon"
          >
            Bulk Upload FAQs via Excel
          </Typography>
          <Typography variant="body2" color="#666666" fontFamily="Karbon">
            Upload an Excel or CSV file with your FAQs. The file should have two
            columns: Question and Answer.
          </Typography>
        </Box>

        <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
          <Button
            variant="contained"
            component="label"
            startIcon={
              <i className="bi bi-download" style={{ color: "#ffffff" }}></i>
            }
            onClick={handleDownloadTemplate}
            disabled={showingLoader}
            sx={{
              bgcolor: "#F25D21",
              color: "#fffff",
              textTransform: "none",
              borderRadius: "8px",
              px: 3,
              py: 1.2,
              fontFamily: "Karbon",
              "&:hover": {
                bgcolor: "#F25D21",
              },
              "&:disabled": {
                bgcolor: "#ffc7b3",
              },
            }}
          >
            Download Template
          </Button>

          <Button
            variant="contained"
            component="label"
            startIcon={
              <i
                className="bi bi-file-earmark-excel"
                style={{ color: "#ffffff" }}
              ></i>
            }
            disabled={showingLoader}
            sx={{
              bgcolor: "#ff6b35",
              color: "#fffff",
              textTransform: "none",
              borderRadius: "8px",
              px: 3,
              py: 1.2,
              fontFamily: "Karbon",
              "&:hover": {
                bgcolor: "#ff5722",
              },
              "&:disabled": {
                bgcolor: "#ffc7b3",
              },
            }}
          >
            Select File
            <input
              type="file"
              hidden
              accept=".xlsx,.xls,.csv"
              onChange={handleFileChange}
              disabled={showingLoader}
            />
          </Button>

          {failedItems.length > 0 && (
            <Button
              variant="contained"
              startIcon={
                <i
                  className="bi bi-file-earmark-arrow-down"
                  style={{ color: "#ffffff" }}
                ></i>
              }
              onClick={handleDownloadFailedItems}
              disabled={showingLoader}
              sx={{
                bgcolor: "#f15a22",
                color: "#ffffff",
                textTransform: "none",
                borderRadius: "8px",
                px: 3,
                py: 1.2,
                fontFamily: "Karbon",
                "&:hover": {
                  bgcolor: "#f15a22",
                },
                "&:disabled": {
                  bgcolor: "#f5c2c7",
                },
              }}
            >
              Download Failed Items
            </Button>
          )}
        </Box>

        {showingLoader && (
          <Box sx={{ width: "100%", mb: 3 }}>
            <LinearProgress
              sx={{
                height: 8,
                borderRadius: 4,
                bgcolor: "#ffe5db",
                "& .MuiLinearProgress-bar": {
                  bgcolor: "#ff6b35",
                },
              }}
            />
            <Typography
              variant="body2"
              color="#666666"
              align="center"
              sx={{ mt: 1 }}
              fontFamily="Karbon"
            >
              Uploading {previewData.length} FAQ(s)...
            </Typography>
          </Box>
        )}

        <Box sx={{ mb: 3 }}>
          <Typography
            variant="body1"
            sx={{
              mb: 1,
              fontFamily: "Karbon",
              color: "#666666",
            }}
          >
            Important Instructions:
          </Typography>
          <Typography
            variant="body1"
            component="div"
            color="#F25D21"
            sx={{ lineHeight: 1.8, fontFamily: "Karbon" }}
          >
            • Accepted file formats:{" "}
            <strong style={{ fontWeight: 100 }}>.xlsx</strong>,{" "}
            <strong style={{ fontWeight: 100 }}>.xls</strong>,{" "}
            <strong style={{ fontWeight: 100 }}>.csv</strong>
            <br />• The file must contain two columns:{" "}
            <strong style={{ fontWeight: 100 }}>question</strong> and{" "}
            <strong style={{ fontWeight: 100 }}>answer</strong>
            <br />• Both question and answer fields are mandatory
          </Typography>
        </Box>

        {/* Failed Items Alert */}
        {/* {failedItems.length > 0 && (
          <Alert
            severity="error"
            sx={{ mb: 3, borderRadius: "8px", fontFamily: "Karbon" }}
            onClose={() => setFailedItems([])}
          >
            <Typography
              variant="body2"
              fontWeight={600}
              sx={{ mb: 1, fontFamily: "Karbon" }}
            >
              {failedItems.length} FAQ(s) failed to upload:
            </Typography>
            <Box sx={{ maxHeight: 200, overflowY: "auto" }}>
              {failedItems.map((item: any, index: number) => (
                <Box key={index} sx={{ mb: 1, pl: 2 }}>
                  <Typography
                    variant="caption"
                    component="div"
                    fontFamily="Karbon"
                  >
                    <strong>Row {item.row || index + 1}:</strong>{" "}
                    {item.error || item.reason || "Unknown error"}
                  </Typography>
                  {(item.data?.question || item.question) && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontFamily="Karbon"
                    >
                      Question: {item.data?.question || item.question}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          </Alert>
        )} */}

        {selectedFile && previewData.length === 0 && !showingLoader && (
          <Alert
            severity="info"
            sx={{ mb: 3, borderRadius: "8px", fontFamily: "Karbon" }}
          >
            <Typography variant="body2" fontFamily="Karbon">
              <strong>Processing file:</strong> {fileName}
            </Typography>
          </Alert>
        )}

        {previewData.length > 0 ? (
          <>
            {selectedFile && (
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="body1"
                  sx={{ mb: 1, fontFamily: "Karbon", color: "#666666" }}
                >
                  Selected File:
                </Typography>
                <Paper
                  sx={{
                    p: 2,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    bgcolor: "#f15a22",
                    border: " #f5f5f5",
                    borderRadius: "8px",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <i
                      className="bi bi-file-earmark-spreadsheet"
                      style={{ fontSize: 24, color: "#ffffff" }}
                    ></i>
                    <Box>
                      <Typography
                        variant="body1"
                        fontFamily="Karbon"
                        color="#ffffff"
                      >
                        {selectedFile.name}
                      </Typography>
                      <Typography
                        variant="caption"
                        fontFamily="Karbon"
                        color="#ffffff"
                      >
                        {failedItems.length > 0
                          ? `${previewData.length} Failed FAQ(s)`
                          : `${previewData.length} FAQ(s) ready to upload`}
                      </Typography>
                    </Box>
                  </Box>
                  <IconButton
                    onClick={handleRemoveFile}
                    size="small"
                    disabled={showingLoader}
                    sx={{ color: "#d32f2f" }}
                  >
                    <i
                      className="bi bi-trash fs-4"
                      style={{ color: "#ffffff" }}
                    ></i>
                  </IconButton>
                </Paper>
              </Box>
            )}

            <Box sx={{ mb: 3 }}>
              <Typography
                variant="h6"
                color="#666666"
                sx={{ mb: 2 }}
                fontFamily="Karbon"
              >
                {failedItems.length > 0 ? "Failed Items" : "Preview"} (
                {previewData.length} FAQ
                {previewData.length !== 1 ? "s" : ""})
              </Typography>

              {previewData.some((item) => item.hasError) && (
                <Alert
                  severity="warning"
                  sx={{ mb: 2, borderRadius: "8px", fontFamily: "Karbon" }}
                >
                  <Typography variant="body2" fontFamily="Karbon">
                    <strong>Warning:</strong>{" "}
                    {failedItems.length > 0
                      ? "These items failed to upload. Please correct the errors and try uploading again."
                      : "Some rows have missing question or answer fields (highlighted in yellow). These may fail during upload."}
                  </Typography>
                </Alert>
              )}

              <TableContainer
                component={Paper}
                sx={{
                  maxHeight: 400,
                  borderRadius: "12px",
                  border: "1px solid #f0e5e0",
                }}
              >
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          width: 80,
                          bgcolor: "#ffe5db",
                          color: "#666666",
                          fontFamily: "Karbon",
                        }}
                      >
                        {failedItems.length > 0 ? "Row #" : "Sr.No."}
                      </TableCell>
                      <TableCell
                        sx={{
                          width: "40%",
                          bgcolor: "#ffe5db",
                          color: "#666666",
                          fontFamily: "Karbon",
                        }}
                      >
                        Question
                      </TableCell>
                      <TableCell
                        sx={{
                          width: "60%",
                          bgcolor: "#ffe5db",
                          color: "#666666",
                          fontFamily: "Karbon",
                        }}
                      >
                        Answer
                      </TableCell>
                      {failedItems.length > 0 && (
                        <TableCell
                          sx={{
                            width: "30%",
                            bgcolor: "#ffe5db",
                            color: "#666666",
                            fontFamily: "Karbon",
                          }}
                        >
                          Error
                        </TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {previewData.map((item: any, index: number) => (
                      <TableRow
                        key={index}
                        hover
                        sx={{
                          "&:hover": { bgcolor: "#fff5f2" },
                          bgcolor: item.hasError ? "#fff3cd" : "inherit",
                        }}
                      >
                        <TableCell
                          sx={{ fontFamily: "karbon", color: "#666666" }}
                        >
                          {failedItems.length > 0 ? item.rowNumber : index + 1}
                        </TableCell>
                        <TableCell
                          sx={{
                            fontFamily: "karbon",
                            color: !item.question ? "#dc3545" : "#666666",
                            fontStyle: !item.question ? "italic" : "normal",
                          }}
                        >
                          {item.question || "(Missing question)"}
                        </TableCell>
                        <TableCell
                          sx={{
                            fontFamily: "karbon",
                            color: !item.answer ? "#dc3545" : "#666666",
                            fontStyle: !item.answer ? "italic" : "normal",
                          }}
                        >
                          {item.answer || "(Missing answer)"}
                        </TableCell>
                        {failedItems.length > 0 && (
                          <TableCell
                            sx={{
                              fontFamily: "karbon",
                              color: "#dc3545",
                              fontSize: "0.875rem",
                            }}
                          >
                            {item.errorMessage}
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 2,
                pt: 2,
              }}
            >
              <button
                type="reset"
                className="btn btn-sm btn-secondary me-2 "
                onClick={handleRemoveFile}
                disabled={showingLoader}
                style={{
                  color: "#666666",
                  fontFamily: "karbon",
                  fontSize: "13px",
                }}
              >
                Cancel
              </button>

              {failedItems.length === 0 && (
                <Button
                  type="submit"
                  variant="contained"
                  disabled={showingLoader || !selectedFile}
                  sx={{
                    bgcolor: "#ff6b35",
                    color: "#fff",
                    textTransform: "none",
                    borderRadius: "8px",
                    px: 4,
                    fontSize: "13px",
                    fontFamily: "Karbon",
                    "&:hover": {
                      bgcolor: "#ff5722",
                    },
                    "&:disabled": {
                      bgcolor: "#ffc7b3",
                    },
                  }}
                >
                  Upload {previewData.length} FAQ
                  {previewData.length !== 1 ? "s" : ""}
                </Button>
              )}
            </Box>
          </>
        ) : (
          <>
            {!selectedFile && !showingLoader && (
              <Paper
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  p: 6,
                  border: "2px dashed #f0e5e0",
                  borderRadius: "12px",
                  bgcolor: "#fff",
                  minHeight: 300,
                }}
              >
                <i
                  className="bi bi-file-earmark-spreadsheet"
                  style={{ fontSize: 64, color: "#ffc7b3", marginBottom: 16 }}
                ></i>
                <Typography
                  variant="h6"
                  color="#666666"
                  gutterBottom
                  fontFamily="Karbon"
                >
                  No file selected
                </Typography>
                <Typography
                  color="#666666"
                  align="center"
                  sx={{ maxWidth: 400 }}
                  fontFamily="Karbon"
                >
                  Upload an Excel (.xlsx, .xls) or CSV file to bulk import FAQs.
                  Make sure your file has two columns: Question and Answer.
                </Typography>
              </Paper>
            )}
          </>
        )}
      </Box>
    </form>
  );
};

export default BulkUpload;
